import React, { useState, useEffect, useRef } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ChartHeader from "./ChartHeader";
import { setDashboardCustomizerUpdate, setOrderCustomization, updateCustomizationFilter } from "../actions/detailsPageActions";
import { connect } from "react-redux";

function ParetoChart({
                         title,
                         subtitle,
                         data,
                         dataColor,
                         lineColor,
                         detailsDesc,
                         orderNumber = "",
                         dateRange = "",
                         customizationKey,           // ← NOWY: klucz dla customization (np. "14")
                         orderName,                  // ← NOWY: potrzebny do setOrderCustomization
                         customizations = [],        // ← NOWY: z Redux
                         setDashboardCustomizerUpdateData,  // ← istniejąca akcja
                         updateCustomizationFilterData,       // ← NOWA akcja
                         customerId,
                         lastParam
                     }) {
    // ==================== FIND CUSTOMIZATION ITEM ====================
    const customizationItem = customizations.find(item => item.key === customizationKey);

    // ==================== STATE FILTRY ====================
    // Pobierz wartości z Redux lub użyj default'ów
    const [maxBars, setMaxBars] = useState(customizationItem?.maxBar || '');
    const [minPercentageThreshold, setMinPercentageThreshold] = useState(customizationItem?.minPercent || '');

    // Ref do śledzenia poprzednich wartości filtrów
    const previousFiltersRef = useRef({ maxBars: 0, minPercent: 0 });
    const debounceTimerRef = useRef(null);

    useEffect(() => {
        if (customizationItem) {
            setMaxBars(customizationItem.maxBar || '');
            setMinPercentageThreshold(customizationItem.minPercent || '');
            previousFiltersRef.current = {
                maxBars: customizationItem.maxBar || '',
                minPercent: customizationItem.minPercent || ''
            };
        }
    }, [customizationItem?.maxBar, customizationItem?.minPercent, customizationItem]);


    // ==================== SYNC Z REDUX + AUTO-SAVE DO BACKENDU ====================
    useEffect(() => {
        // Sprawdź czy wartości faktycznie się zmieniły
        const hasChanged = previousFiltersRef.current.maxBars !== maxBars ||
            previousFiltersRef.current.minPercent !== minPercentageThreshold;
        if (!hasChanged) {
            return;  // Nie rób nic jeśli wartości się nie zmieniły
        }

        // Zaktualizuj ref z nowymi wartościami
        previousFiltersRef.current = { maxBars, minPercent: minPercentageThreshold };

        // Wyczyść poprzedni timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Ustaw nowy debounced timer
        debounceTimerRef.current = setTimeout(() => {
            const conditions = {
                customizationKey: !!customizationKey,
                updateCustomizationFilterData: !!updateCustomizationFilterData,
                setDashboardCustomizerUpdateData: !!setDashboardCustomizerUpdateData,
                orderName: !!orderName
            };

            if (customizationKey && updateCustomizationFilterData && setDashboardCustomizerUpdateData && orderName) {
                // 1. Aktualizuj Redux state
                updateCustomizationFilterData(customizationKey, {
                    maxBar: maxBars,
                    minPercent: minPercentageThreshold
                });

                // 2. Od razu wysyłaj do backendu ze zaktualizowanymi danymi (ręcznie zmerguj zmianę)
                const updatedCustomizations = customizations.map(item =>
                    item.key === customizationKey
                        ? { ...item, maxBar: maxBars, minPercent: minPercentageThreshold }
                        : item
                );

                setDashboardCustomizerUpdateData(
                    updatedCustomizations,
                    lastParam,
                    customerId
                );
            }
        }, 1000);

        // Cleanup
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [maxBars, minPercentageThreshold, customizationKey, updateCustomizationFilterData, setDashboardCustomizerUpdateData, orderName, customizations]);

    // ==================== DANE ORYGINALNE (dla statystyk) ====================
    const defaultData = { A: "n/a" };
    const inputData = data || defaultData;

    const allFilteredEntries = Object.entries(inputData).filter(([key, value]) => key !== 'mainField' && value > 0);
    const allSortedEntries = allFilteredEntries.sort(([,a], [,b]) => b - a);
    const totalCount = allSortedEntries.reduce((sum, [key, value]) => sum + value, 0);

    // Przygotuj wszystkie dane (bez filtrów) - dla statystyk
    let allCumulativeCount = 0;
    const allChartData = allSortedEntries.map(([name, count]) => {
        allCumulativeCount += count;
        const percentage = Math.round((count / totalCount) * 100 * 10) / 10;
        const cumulativePercentage = Math.round((allCumulativeCount / totalCount) * 100 * 10) / 10;

        return {
            name,
            count,
            percentage,
            cumulativeCount: allCumulativeCount,
            cumulativePercentage
        };
    });

    // ==================== DANE FILTROWANE (dla wykresu) ====================
    // Filtruj na podstawie minimalnego progu procentowego
    let filteredChartData = allChartData.filter(item => item.percentage >= minPercentageThreshold);

    // Filtruj na podstawie liczby słupków (top N)
    if (maxBars > 0) {
        filteredChartData = filteredChartData.slice(0, maxBars);
    }

    // Recalculate cumulative dla filtrowanych danych (ale zawsze z totalCount ze WSZYSTKICH danych)
    let cumulativeCountFiltered = 0;
    const chartData = filteredChartData.map(item => {
        cumulativeCountFiltered += item.count;
        const cumulativePercentageFiltered = Math.round((cumulativeCountFiltered / totalCount) * 100 * 10) / 10;

        return {
            ...item,
            cumulativeCount: cumulativeCountFiltered,
            cumulativePercentage: cumulativePercentageFiltered
        };
    });

    // ==================== STATYSTYKI (na podstawie FILTROWANYCH danych) ====================
    const filteredTotalCount = chartData.reduce((sum, item) => sum + item.count, 0);
    const filteredDefectTypes = chartData.length;
    const mostFrequentDefect = chartData.length > 0 ? chartData[0].name : 'N/A';
    const mainImpact = chartData.length > 0 ? chartData[0].cumulativePercentage : '0%';

    // ==================== KONFIGURACJA WYKRESU ====================
    const defectCount = chartData.length;
    let xAxisFontSize = 12;
    let xAxisAngle = 0;

    if (defectCount > 25) {
        xAxisFontSize = 7;
        xAxisAngle = -90;
    } else if (defectCount > 15) {
        xAxisFontSize = 9;
    }

    // ==================== CUSTOM COMPONENTS ====================
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload.find(p => p.dataKey === 'count');
            const cumulativeData = payload.find(p => p.dataKey === 'cumulativePercentage');

            if (data) {
                return (
                    <div style={{
                        backgroundColor: 'white',
                        padding: '12px 16px',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        fontSize: '13px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}>
                        <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: 'rgb(52, 71, 103)' }}>
                            {label} : {data.payload.count} ({data.payload.percentage}%) {
                            detailsDesc && detailsDesc
                                .find(el => el.label === label)
                                ?.value
                        }
                        </p>
                        <p style={{ margin: '0 0 4px 0', color: '#666' }}>
                            Occurrences: <strong>{data.payload.count}</strong>
                        </p>
                        <p style={{ margin: '0 0 4px 0', color: '#666' }}>
                            Percentage: <strong>{data.payload.percentage}%</strong>
                        </p>
                        <p style={{ margin: '0', color: (lineColor ? lineColor : '#E91E63') }}>
                            Cumulative: <strong>{cumulativeData?.payload.cumulativePercentage}%</strong>
                        </p>
                    </div>
                );
            }
        }
        return null;
    };

    const CustomLegend = ({ payload }) => {
        return (
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '12px',
                maxHeight: '80px',
                overflowY: 'auto',
                paddingRight: '8px',
                paddingBottom: '4px',
                marginTop: '12px'
            }}>
                {chartData.map((item, index) => (
                    <div key={index} style={{
                        fontSize: '12px',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        color: 'rgb(52, 71, 103)',
                        fontWeight: '500'
                    }}>
                        {item.name} : {item.count} ({item.percentage}%) {
                        detailsDesc && detailsDesc
                            .find(el => el.label === item.name)
                            ?.value
                    }
                    </div>
                ))}
            </div>
        );
    };

    // ==================== STYLES ====================
    const styles = {
        container: {
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            padding: '20px',
            margin: '16px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(0, 0, 0, 0.06)'
        },
        header: {
            marginBottom: '16px'
        },
        filterContainer: {
            display: 'flex',
            gap: '20px',
            padding: '16px',
            marginBottom: '16px',
            backgroundColor: '#f5f7fa',
            borderRadius: '6px',
            alignItems: 'flex-end',
            flexWrap: 'wrap'
        },
        filterItem: {
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            alignItems: 'center',
            minWidth: 'auto'
        },
        filterLabel: {
            fontSize: '12px',
            fontWeight: '600',
            color: 'rgb(52, 71, 103)',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
            whiteSpace: 'nowrap'
        },
        filterInput: {
            padding: '2px 8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '13px',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
            color: 'rgb(52, 71, 103)',
            transition: 'border-color 0.2s',
            outline: 'none',
            width: '42px',
            marginTop: '-4px'
        },
        chartContainer: {
            backgroundColor: 'rgb(248, 249, 250)',
            borderRadius: '6px',
            padding: '18px',
            paddingBottom: '50px',  // ← Miejsce dla absolute filtrów
            marginBottom: '0px',
            minHeight: '280px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
        },
        statsContainer: {
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            marginBottom: '16px',
            padding: '0 20px'
        },
        statItem: {
            textAlign: 'center'
        },
        statNumber: {
            fontSize: '18px',
            fontWeight: '600',
            color: 'rgb(52, 71, 103)',
            margin: '0',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        },
        statLabel: {
            fontSize: '11px',
            color: 'rgb(123, 128, 154)',
            margin: '0',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        }
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <ChartHeader
                title={title}
                subtitle={subtitle}
                orderName={orderNumber}
                dateRange={dateRange}
            />

            {/* Wykres i statystyki */}
            <div style={styles.chartContainer}>
                {/* 📊 STATYSTYKI (z FILTROWANYCH danych) */}
                <div style={styles.statsContainer}>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{filteredDefectTypes}</p>
                        <p style={styles.statLabel}>Defect Types (Filtered)</p>
                    </div>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{filteredTotalCount}</p>
                        <p style={styles.statLabel}>Total Occurrences</p>
                    </div>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>{mostFrequentDefect}</p>
                        <p style={styles.statLabel}>Most Frequent Defect</p>
                    </div>
                    <div style={styles.statItem}>
                        <p style={styles.statNumber}>
                            {mainImpact}
                        </p>
                        <p style={styles.statLabel}>Main Impact</p>
                    </div>
                </div>

                {/* 📈 PARETO CHART (z filtrowanymi danymi) */}
                {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={352}>
                        <ComposedChart
                            data={chartData}
                            margin={{
                                top: 15,
                                right: 30,
                                bottom: 30,
                                left: 20,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                angle={xAxisAngle}
                                textAnchor={xAxisAngle !== 0 ? "end" : "middle"}
                                height={xAxisAngle !== 0 ? 40 : 20}
                                tick={{ fontSize: xAxisFontSize, fill: 'rgb(123, 128, 154)' }}
                            />
                            <YAxis
                                yAxisId="left"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: 'rgb(123, 128, 154)' }}
                                label={{ value: 'Count', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                domain={[0, 100]}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: 'rgb(123, 128, 154)' }}
                                label={{ value: 'Cumulative %', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend content={<CustomLegend />} />
                            <Bar
                                yAxisId="left"
                                dataKey="count"
                                fill={dataColor ? dataColor : "#4CAF50"}
                                radius={[4, 4, 0, 0]}
                                name="count"
                            />
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="cumulativePercentage"
                                stroke={(lineColor ? lineColor : '#E91E63')}
                                strokeWidth={3}
                                dot={{ fill: (lineColor ? lineColor : '#E91E63'), strokeWidth: 2, r: 4 }}
                                activeDot={{ r: 6, stroke: (lineColor ? lineColor : '#E91E63'), strokeWidth: 2, fill: '#fff' }}
                                name="cumulativePercentage"
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                ) : (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '352px',
                        color: '#999',
                        fontSize: '14px'
                    }}>
                        No data matches the current filters
                    </div>
                )}

                {/* 🎛️ FILTRY (position absolute - overlaid na dole wykresu) */}
                <div style={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    border: '5px solid white',
                    backgroundColor: 'white',
                    width: '100%'
                }}>
                    {/* Filter 1: Max Bars */}
                    <div style={styles.filterItem}>
                        <label style={styles.filterLabel}>Max bar:</label>
                        <input
                            type="number"
                            min="0"
                            max={allChartData.length}
                            value={maxBars}
                            onChange={(e) => setMaxBars(Math.max(0, parseInt(e.target.value) || ''))}
                            style={styles.filterInput}
                            onFocus={(e) => {
                                e.target.select();
                                e.target.style.borderColor = '#4CAF50';
                            }}
                            onBlur={(e) => e.target.style.borderColor = '#ddd'}
                        />
                    </div>

                    {/* Filter 2: Percentage Threshold */}
                    <div style={styles.filterItem}>
                        <label style={styles.filterLabel}>Min %:</label>
                        <input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            max="100"
                            step="any"
                            value={minPercentageThreshold}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || val === '.' || val === '-') {
                                    setMinPercentageThreshold(0);
                                } else {
                                    const num = parseFloat(val);
                                    if (!isNaN(num)) {
                                        setMinPercentageThreshold(Math.max(0, Math.min(100, num)));
                                    }
                                }
                            }}
                            onFocus={(e) => {
                                e.target.select();
                                e.target.style.borderColor = '#2196F3';
                            }}
                            onBlur={(e) => e.target.style.borderColor = '#ddd'}
                            style={styles.filterInput}
                        />
                    </div>

                    {/* Info o aktualnym filtrze */}
                    <div style={{ fontSize: '11px', color: 'rgb(123, 128, 154)', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
                        <strong>{filteredDefectTypes}</strong> / <strong>{allChartData.length}</strong>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default connect(state => ({
    customizations: state.clientPanel.detailsPageReducer.customizations || [],
}), (dispatch) => ({
    setDashboardCustomizerUpdateData: (freshCustomizations, lastParam, customerId) => {
        dispatch(setDashboardCustomizerUpdate(freshCustomizations))
        dispatch(setOrderCustomization(lastParam, customerId))
    },
    updateCustomizationFilterData: (key, filterData) => {
        dispatch(updateCustomizationFilter(key, filterData))
    }
}))(ParetoChart);